import React, { useState } from 'react';
import {
  Printer,
  Download,
  X,
  CheckCircle2,
  Award,
  FileText,
  ShieldCheck,
  CreditCard,
  User,
  Calendar,
  Clock,
  MapPin,
  Building2
} from 'lucide-react';
import './EnglishAdmissionsDocuments.css';

function formatFormalDate(dateStr) {
  if (!dateStr) return new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch (_) {
    return dateStr;
  }
}

function formatCurrencyIDR(amount) {
  const num = Number(amount || 0);
  return 'IDR ' + num.toLocaleString('en-US');
}

/**
 * 1. OFFICIAL LETTER OF ACCEPTANCE (LoA)
 * Formal English Admission Acceptance Letter
 */
export function LetterOfAcceptanceModal({ app, onClose }) {
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  if (!app) return null;

  const childName = app.child_full_name || app.student_name || app.name || 'Student Candidate';
  const regNo = app.registration_no || `REG-PHS-${app.id || '0001'}`;
  const loaNumber = app.loa_number || `LOA-PHS/2026/${String(app.id || '0001').padStart(4, '0')}`;
  const issuedDate = formatFormalDate(app.loa_issued_at || app.updated_at);
  const academicYear = app.academic_year || '2026/2027';
  const gradeTarget = app.grade_target || app.level_name || 'Primary School';
  const campus = app.campus_location || 'Campus 1 (Pos Pengumben) - Jl. Pos Pengumben Lama No.13';
  const levelName = app.level || app.level_name || 'Sekolah Permata Harapan';

  const formData = app.form_data || {};
  const parentInfo = app.parent_info || {};

  // Child detailed particulars
  const birthPlace = app.birth_place || formData.birth_place || formData.place_of_birth || '';
  const rawBirthDate = app.birth_date || formData.birth_date || formData.date_of_birth || '';
  const birthDateFormatted = rawBirthDate ? formatFormalDate(rawBirthDate) : '';
  const birthPlaceDate = [birthPlace, birthDateFormatted].filter(Boolean).join(', ') || '-';

  const genderRaw = app.gender || formData.gender || formData.jenis_kelamin || '';
  const gender = (genderRaw === 'male' || genderRaw === 'L' || String(genderRaw).toLowerCase() === 'laki-laki') ? 'Laki-laki (Male)'
    : (genderRaw === 'female' || genderRaw === 'P' || String(genderRaw).toLowerCase() === 'perempuan') ? 'Perempuan (Female)'
    : (genderRaw || '-');

  const nationality = app.nationality || formData.nationality || formData.kewarganegaraan || 'WNI (Indonesia)';
  const religion = app.religion || formData.religion || formData.agama || '-';
  const previousSchool = app.previous_school || formData.previous_school || formData.asal_sekolah || '-';

  // Parent / Guardian detailed particulars
  const parentName = parentInfo.full_name || app.applicant_name || formData.parent_name || 'Parent / Guardian';
  const fatherName = parentInfo.father_name || formData.father_name || formData.nama_ayah || '-';
  const motherName = parentInfo.mother_name || formData.mother_name || formData.nama_ibu || '-';
  const parentPhone = parentInfo.phone || app.applicant_phone || formData.phone || formData.whatsapp || '-';
  const parentEmail = parentInfo.email || app.applicant_email || app.email || formData.email || '-';
  const parentAddress = parentInfo.home_address || formData.home_address || formData.address || formData.alamat || '-';
  const parentOccupation = formData.father_occupation || formData.mother_occupation || formData.parent_job || formData.pekerjaan || '-';

  // Signatories
  const officerName = app.admissions_officer_name || 'Ivana';
  const officerRole = 'Admissions Officer';
  const directorName = app.school_director_name || app.school_principal_name || (['preschool', 'kindergarten'].includes(String(app.level_code || '').toLowerCase()) ? 'Ratna Sari, S.Pd., M.Pd' : 'Asnah Roidah Yulianti, S.Si., M.Pd');
  const rawDirectorRole = app.school_director_title || app.school_principal_title || 'School Director';
  const directorRole = rawDirectorRole === 'School Principal' ? 'School Director' : rawDirectorRole;

  // Complete Fee Components without exception
  const inv = app.invoice || {};
  const lvlCfg = app.level_config || {};

  const regFee = Number(inv.registration_fee ?? lvlCfg.registration_fee ?? 250000);
  const testFee = Number(inv.test_reg_form_fee ?? lvlCfg.test_reg_form_fee ?? 250000);
  const admissionFee = Number(inv.admission_fee ?? lvlCfg.admission_fee ?? 1500000);
  const devFee = Number(inv.development_fee ?? lvlCfg.development_fee ?? 12000000);

  const df1 = Number(inv.df_1_amount ?? lvlCfg.df_1_amount ?? (devFee * 0.4));
  const df2 = Number(inv.df_2_amount ?? lvlCfg.df_2_amount ?? (devFee * 0.3));
  const df3 = Number(inv.df_3_amount ?? lvlCfg.df_3_amount ?? (devFee * 0.15));
  const df4 = Number(inv.df_4_amount ?? lvlCfg.df_4_amount ?? (devFee * 0.15));

  const booksFee = Number(inv.books_activity_fee ?? lvlCfg.books_activity_fee ?? 2000000);
  const tuitionFee = Number(inv.tuition_fee ?? lvlCfg.tuition_fee ?? 1800000);

  const discountAmount = Number(inv.discount_amount ?? app.discount_amount ?? 0);
  const discountLabel = inv.discount_label || app.promo_code_used || 'Approved Incentive';

  const subtotalFees = regFee + testFee + admissionFee + devFee + booksFee + tuitionFee;
  const totalPayable = Number(inv.total_payable || (subtotalFees - discountAmount));

  const bankName = inv.bank_name || 'Bank Central Asia (BCA)';
  const bankAccount = inv.bank_account_number || '253 388 9988';
  const bankHolder = inv.bank_account_holder || 'Yayasan Permata Harapan Bangsa';

  const handlePrint = () => {
    const originalTitle = document.title;
    document.title = `Letter_of_Acceptance_${childName.replace(/\s+/g, '_')}`;
    window.print();
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  const handleDownloadPdf = async () => {
    const containerEl = document.querySelector('.ead-loa-pdf-export-container');
    if (!containerEl) return;
    try {
      setDownloadingPdf(true);
      const html2pdf = (await import('html2pdf.js')).default;
      const opt = {
        margin: [0, 0, 0, 0],
        filename: `Letter_of_Acceptance_${childName.replace(/\s+/g, '_')}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'], after: '.ead-loa-page-1' }
      };
      await html2pdf().set(opt).from(containerEl).save();
    } catch (err) {
      console.error('PDF error, fallback to print:', err);
      handlePrint();
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="ead-modal-overlay" onClick={onClose}>
      <div className="ead-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="ead-modal-toolbar">
          <div className="ead-toolbar-title">
            <Award size={18} />
            <span>Letter of Acceptance (LoA) - {childName}</span>
          </div>
          <div className="ead-toolbar-actions">
            <button
              type="button"
              className="ead-btn-download"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
            >
              <Download size={15} />
              <span>{downloadingPdf ? 'Menyimpan PDF...' : 'Simpan / Unduh PDF (.pdf)'}</span>
            </button>
            <button type="button" className="ead-btn-print" onClick={handlePrint}>
              <Printer size={15} />
              <span>Cetak Dokumen</span>
            </button>
            <button type="button" className="ead-btn-close" onClick={onClose} aria-label="Tutup">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="ead-paper-scroll">
          <div className="ead-loa-pdf-export-container">
            {/* ===================================================================
                HALAMAN 1 (PAGE 1 OF 2): SURAT PENERIMAAN RESMI & DATA LENGKAP
            =================================================================== */}
            <div className="ead-sheet ead-sheet-loa-page ead-loa-page-1">
              <div>
                {/* KOP SURAT FORMAL (LPTI UNPAM CONCEPT) */}
                <div className="ead-kop-surat">
                  <div className="ead-kop-inner">
                    <div className="ead-kop-logo-box">
                      <img
                        src="/logo_phs.png"
                        alt="Logo Sekolah Permata Harapan"
                        className="ead-kop-logo"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="ead-logo-badge" style={{ display: 'none' }}>PHS</div>
                    </div>
                    <div className="ead-kop-text">
                      <div className="ead-kop-foundation">YAYASAN PENDIDIKAN PERMATA HARAPAN</div>
                      <div className="ead-kop-school">SEKOLAH PERMATA HARAPAN</div>
                      <div className="ead-kop-unit">ADMISSIONS &amp; ACADEMIC ENROLLMENT DIRECTORATE</div>
                      <div className="ead-kop-address">
                        Jl. Pos Pengumben Lama No. 13, Srengseng, Kembangan, Jakarta Barat, DKI Jakarta 11630
                      </div>
                      <div className="ead-kop-contact">
                        Phone: (021) 5890 1100 • WhatsApp: 0895-4045-01010 • Email: admissions@permataharapansecondary.sch.id
                      </div>
                    </div>
                  </div>
                  <div className="ead-kop-divider" />
                </div>

                {/* Reference & Date */}
                <div className="ead-meta-bar" style={{ marginBottom: '10px' }}>
                  <div><strong>Nomor Surat / Ref:</strong> {loaNumber}</div>
                  <div><strong>Tanggal Terbit:</strong> {issuedDate}</div>
                </div>

                {/* Document Title */}
                <div className="ead-doc-title-block" style={{ marginBottom: '10px' }}>
                  <h2 className="ead-doc-title" style={{ fontSize: '1.25rem', letterSpacing: '0.04em' }}>OFFICIAL LETTER OF ACCEPTANCE</h2>
                  <div className="ead-doc-subtitle" style={{ fontSize: '0.8rem' }}>SURAT KEPUTUSAN PENERIMAAN SISWA BARU • TAHUN AJARAN {academicYear}</div>
                </div>

                {/* Recipient */}
                <div className="ead-salutation" style={{ marginBottom: '8px', fontSize: '0.82rem' }}>
                  Kepada Yth. Orang Tua / Wali Calon Siswa: <strong>{childName}</strong><br />
                  Nomor Registrasi PPDB: <strong>{regNo}</strong>
                </div>

                <p className="ead-paragraph" style={{ marginBottom: '6px', fontSize: '0.82rem' }}>
                  Dengan hormat,
                </p>

                <p className="ead-paragraph" style={{ marginBottom: '10px', fontSize: '0.82rem', lineHeight: '1.45' }}>
                  Berdasarkan hasil evaluasi seleksi observasi dan asesmen kesiapan akademik, Panitia Penerimaan Peserta Didik Baru (PPDB) bersama Direktur Sekolah Permata Harapan menetapkan bahwa putra/putri Bapak/Ibu, <strong>{childName}</strong>, telah memenuhi seluruh standar kualifikasi institusi dan <strong>DINYATAKAN DITERIMA</strong> sebagai calon siswa di Sekolah Permata Harapan untuk Tahun Ajaran <strong>{academicYear}</strong>.
                </p>

                {/* 1. DATA LENGKAP CALON SISWA */}
                <div className="ead-section-heading" style={{ margin: '8px 0 5px' }}>
                  <User size={13} />
                  <span>1. Particulars of Accepted Student (Data Lengkap Calon Siswa)</span>
                </div>
                <div className="ead-highlight-box" style={{ padding: '8px 12px', marginBottom: '8px' }}>
                  <div className="ead-grid-2" style={{ gap: '5px 12px' }}>
                    <div className="ead-grid-item">
                      <span className="ead-grid-label">Nama Lengkap Siswa</span>
                      <span className="ead-grid-value">{childName}</span>
                    </div>
                    <div className="ead-grid-item">
                      <span className="ead-grid-label">Nomor Registrasi (ID)</span>
                      <span className="ead-grid-value">{regNo}</span>
                    </div>
                    <div className="ead-grid-item">
                      <span className="ead-grid-label">Jenjang &amp; Kelas Diterima</span>
                      <span className="ead-grid-value">{gradeTarget} ({levelName})</span>
                    </div>
                    <div className="ead-grid-item">
                      <span className="ead-grid-label">Tahun Ajaran &amp; Kampus</span>
                      <span className="ead-grid-value">TA {academicYear} • {campus}</span>
                    </div>
                    <div className="ead-grid-item">
                      <span className="ead-grid-label">Tempat &amp; Tanggal Lahir</span>
                      <span className="ead-grid-value">{birthPlaceDate}</span>
                    </div>
                    <div className="ead-grid-item">
                      <span className="ead-grid-label">Jenis Kelamin</span>
                      <span className="ead-grid-value">{gender}</span>
                    </div>
                    <div className="ead-grid-item">
                      <span className="ead-grid-label">Kewarganegaraan &amp; Agama</span>
                      <span className="ead-grid-value">{nationality} • {religion}</span>
                    </div>
                    <div className="ead-grid-item">
                      <span className="ead-grid-label">Asal Sekolah Sebelumnya</span>
                      <span className="ead-grid-value">{previousSchool}</span>
                    </div>
                  </div>
                </div>

                {/* 2. DATA LENGKAP ORANG TUA / WALI */}
                <div className="ead-section-heading" style={{ margin: '8px 0 5px' }}>
                  <Building2 size={13} />
                  <span>2. Parent / Guardian Particulars (Data Lengkap Orang Tua / Wali)</span>
                </div>
                <div className="ead-highlight-box" style={{ padding: '8px 12px', marginBottom: '8px' }}>
                  <div className="ead-grid-2" style={{ gap: '5px 12px' }}>
                    <div className="ead-grid-item">
                      <span className="ead-grid-label">Nama Orang Tua / Wali</span>
                      <span className="ead-grid-value">{parentName}</span>
                    </div>
                    <div className="ead-grid-item">
                      <span className="ead-grid-label">Nama Ayah / Ibu</span>
                      <span className="ead-grid-value">{fatherName} / {motherName}</span>
                    </div>
                    <div className="ead-grid-item">
                      <span className="ead-grid-label">Nomor Telepon / WhatsApp</span>
                      <span className="ead-grid-value">{parentPhone}</span>
                    </div>
                    <div className="ead-grid-item">
                      <span className="ead-grid-label">Alamat Email Terdaftar</span>
                      <span className="ead-grid-value">{parentEmail}</span>
                    </div>
                    <div className="ead-grid-item" style={{ gridColumn: 'span 2' }}>
                      <span className="ead-grid-label">Alamat Domisili Tempat Tinggal</span>
                      <span className="ead-grid-value">{parentAddress}</span>
                    </div>
                    {parentOccupation && parentOccupation !== '-' && (
                      <div className="ead-grid-item" style={{ gridColumn: 'span 2' }}>
                        <span className="ead-grid-label">Pekerjaan Orang Tua / Wali</span>
                        <span className="ead-grid-value">{parentOccupation}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Assessment Result If Available */}
                {app.test_score !== null && app.test_score !== undefined && (
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '4px', padding: '6px 10px', margin: '6px 0', fontSize: '0.8rem' }}>
                    <strong>Hasil Evaluasi Asesmen Masuk:</strong> Dinyatakan <strong>Lulus</strong> dengan capaian nilai{' '}
                    <span style={{ color: '#059669', fontWeight: 700 }}>{app.test_score} / 100</span>.
                    {app.test_evaluation_notes && (
                      <span style={{ fontStyle: 'italic', display: 'block', marginTop: '2px', color: '#166534' }}>
                        Catatan Penguji: &ldquo;{app.test_evaluation_notes}&rdquo;
                      </span>
                    )}
                  </div>
                )}

                {/* Pointer to Page 2 */}
                <div className="ead-loa-page-notice">
                  <strong>Pemberitahuan Administrasi:</strong> Rincian lengkap jadwal seluruh komponen biaya investasi pendidikan resmi, nomor rekening bank sekolah, serta lembar pengesahan tanda tangan tercantum pada <strong>Halaman 2 (Lampiran Resmi LoA)</strong>.
                </div>
              </div>

              {/* FOOTER HALAMAN 1 (LPTI UNPAM CONCEPT) */}
              <div className="ead-kop-footer">
                <div className="ead-kop-footer-divider" />
                <div className="ead-kop-footer-content">
                  <div className="ead-kop-footer-left">
                    <div><strong>Kampus 1:</strong> Jl. Pos Pengumben Lama No. 13, Srengseng, Kembangan, Jakarta Barat 11630</div>
                    <div><strong>Kampus 2:</strong> Jl. Pos Pengumben No. 35, Kebon Jeruk, Jakarta Barat 11530</div>
                    <div><strong>Helpdesk Admissions:</strong> Telp: (021) 5890 1100 • WhatsApp: 0895-4045-01010 • Email: admissions@permataharapansecondary.sch.id</div>
                    <div><strong>Official Portal:</strong> https://permataharapansecondary.sch.id</div>
                  </div>
                  <div className="ead-kop-footer-right">
                    <span className="ead-kop-page-num">- PAGE 1 OF 2 -</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ===================================================================
                HALAMAN 2 (PAGE 2 OF 2): RINCIAN BIAYA RESMI & PENGESAHAN TANDA TANGAN
            =================================================================== */}
            <div className="ead-sheet ead-sheet-loa-page ead-loa-page-2">
              <div>
                {/* KOP SURAT FORMAL HALAMAN 2 (LPTI UNPAM CONCEPT) */}
                <div className="ead-kop-surat">
                  <div className="ead-kop-inner">
                    <div className="ead-kop-logo-box">
                      <img
                        src="/logo_phs.png"
                        alt="Logo Sekolah Permata Harapan"
                        className="ead-kop-logo ead-kop-logo-sm"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="ead-logo-badge" style={{ display: 'none' }}>PHS</div>
                    </div>
                    <div className="ead-kop-text">
                      <div className="ead-kop-foundation" style={{ fontSize: '0.72rem' }}>YAYASAN PENDIDIKAN PERMATA HARAPAN</div>
                      <div className="ead-kop-school" style={{ fontSize: '1.05rem' }}>SEKOLAH PERMATA HARAPAN</div>
                      <div className="ead-kop-unit" style={{ fontSize: '0.75rem' }}>ADMISSIONS &amp; ACADEMIC ENROLLMENT DIRECTORATE</div>
                    </div>
                    <div className="ead-kop-page-tag">
                      <span className="ead-page-badge">- PAGE 2 -</span>
                    </div>
                  </div>
                  <div className="ead-kop-divider" />
                </div>

                {/* Reference Summary Banner */}
                <div className="ead-loa-ref-banner">
                  <div>
                    <strong>Lampiran LoA:</strong> Rincian Biaya Pendidikan &amp; Pengesahan Resmi
                  </div>
                  <div>
                    Ref: <strong>{loaNumber}</strong> • Calon Siswa: <strong>{childName}</strong> ({regNo})
                  </div>
                </div>

                {/* 3. RINCIAN KOMPONEN BIAYA MASUK TANPA TERKECUALI */}
                <div className="ead-section-heading" style={{ margin: '8px 0 6px' }}>
                  <CreditCard size={13} />
                  <span>3. Comprehensive Schedule of Admission Fees &amp; Settlement (IDR)</span>
                </div>
                <table className="ead-table" style={{ marginBottom: '8px' }}>
                  <thead>
                    <tr>
                      <th style={{ width: '36px', padding: '6px 8px' }}>No</th>
                      <th style={{ padding: '6px 8px' }}>Komponen Biaya Resmi Masuk Sekolah</th>
                      <th className="text-right" style={{ width: '180px', padding: '6px 8px' }}>Nominal Biaya (IDR)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '5px 8px' }}>1</td>
                      <td style={{ padding: '5px 8px' }}>
                        <strong>Biaya Formulir Pendaftaran (Registration Form Fee)</strong>
                        <div className="ead-table-termin-note">Biaya administrasi berkas registrasi resmi calon siswa</div>
                      </td>
                      <td className="text-right" style={{ padding: '5px 8px' }}>{formatCurrencyIDR(regFee)}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '5px 8px' }}>2</td>
                      <td style={{ padding: '5px 8px' }}>
                        <strong>Biaya Tes Evaluasi Masuk &amp; Observasi (Assessment &amp; Observation Fee)</strong>
                        <div className="ead-table-termin-note">Pelaksanaan asesmen kesiapan akademik, observasi &amp; wawancara</div>
                      </td>
                      <td className="text-right" style={{ padding: '5px 8px' }}>{formatCurrencyIDR(testFee)}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '5px 8px' }}>3</td>
                      <td style={{ padding: '5px 8px' }}>
                        <strong>Biaya Matrikulasi / Uang Masuk (Admission &amp; Enrollment Fee)</strong>
                        <div className="ead-table-termin-note">Biaya konfirmasi penerimaan dan registrasi status kesiswaan</div>
                      </td>
                      <td className="text-right" style={{ padding: '5px 8px' }}>{formatCurrencyIDR(admissionFee)}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '5px 8px' }}>4</td>
                      <td style={{ padding: '5px 8px' }}>
                        <strong>Uang Pangkal / Development Fee (DF) Total</strong>
                        <div className="ead-table-termin-note">
                          <span>Rincian Termin:</span>
                          <span className="ead-termin-pill">Termin 1: {formatCurrencyIDR(df1)}</span>
                          <span className="ead-termin-pill">Termin 2: {formatCurrencyIDR(df2)}</span>
                          <span className="ead-termin-pill">Termin 3: {formatCurrencyIDR(df3)}</span>
                          {df4 > 0 && <span className="ead-termin-pill">Termin 4: {formatCurrencyIDR(df4)}</span>}
                        </div>
                      </td>
                      <td className="text-right" style={{ padding: '5px 8px' }}>{formatCurrencyIDR(devFee)}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '5px 8px' }}>5</td>
                      <td style={{ padding: '5px 8px' }}>
                        <strong>Paket Buku, Seragam &amp; Aktivitas (Books, Uniform &amp; Activity Fee)</strong>
                        <div className="ead-table-termin-note">Modul belajar, seragam sekolah lengkap &amp; kegiatan penunjang</div>
                      </td>
                      <td className="text-right" style={{ padding: '5px 8px' }}>{formatCurrencyIDR(booksFee)}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '5px 8px' }}>6</td>
                      <td style={{ padding: '5px 8px' }}>
                        <strong>SPP Bulan Pertama (First Month Tuition Fee)</strong>
                        <div className="ead-table-termin-note">Iuran pembinaan pendidikan bulan pertama TA {academicYear}</div>
                      </td>
                      <td className="text-right" style={{ padding: '5px 8px' }}>{formatCurrencyIDR(tuitionFee)}</td>
                    </tr>
                    {discountAmount > 0 && (
                      <tr className="ead-discount-row">
                        <td style={{ padding: '5px 8px' }}>-</td>
                        <td style={{ padding: '5px 8px' }}>
                          <strong>Potongan Promo / Beasiswa Khusus ({discountLabel})</strong>
                          <div className="ead-table-termin-note" style={{ color: '#059669' }}>Insentif potongan resmi yang disahkan pada periode pendaftaran ini</div>
                        </td>
                        <td className="text-right" style={{ padding: '5px 8px' }}>-{formatCurrencyIDR(discountAmount)}</td>
                      </tr>
                    )}
                    <tr className="ead-total-row">
                      <td colSpan="2" style={{ padding: '7px 8px' }}>TOTAL INVESTASI PENDAFTARAN RESMI (TOTAL PAYABLE)</td>
                      <td className="text-right" style={{ padding: '7px 8px' }}>{formatCurrencyIDR(totalPayable)}</td>
                    </tr>
                  </tbody>
                </table>

                {/* Official School Bank Box */}
                <div className="ead-bank-box" style={{ marginTop: '8px', padding: '8px 12px' }}>
                  <div className="ead-bank-box-header">
                    <CreditCard size={12} />
                    <span>Rekening Resmi Pembayaran Sekolah Permata Harapan</span>
                  </div>
                  <div className="ead-bank-box-grid">
                    <div>
                      <div className="ead-bank-item-label">Bank Penerima</div>
                      <div className="ead-bank-item-val">{bankName}</div>
                    </div>
                    <div>
                      <div className="ead-bank-item-label">Nomor Rekening Resmi</div>
                      <div className="ead-bank-item-val">{bankAccount}</div>
                    </div>
                    <div>
                      <div className="ead-bank-item-label">Atas Nama Rekening</div>
                      <div className="ead-bank-item-val">{bankHolder}</div>
                    </div>
                  </div>
                </div>

                <p className="ead-paragraph" style={{ fontSize: '0.78rem', color: '#475569', margin: '8px 0 10px' }}>
                  Untuk mengonfirmasi penerimaan dan mengamankan alokasi kursi siswa, mohon selesaikan konfirmasi pembayaran serta unggah bukti transfer resmi melalui Parent Portal sesuai jadwal batas waktu yang ditentukan.
                </p>

                {/* 4. TIGA KOLOM TANDA TANGAN (PARENT, ADMIN, DIREKTUR SEKOLAH) */}
                <div className="ead-signature-section-3col" style={{ marginTop: '14px', paddingTop: '10px' }}>
                  {/* Kolom 1: Orang Tua / Wali (Dikosongkan untuk tanda tangan basah fisik, nama dicantumkan) */}
                  <div className="ead-sig-col">
                    <div className="ead-sig-col-title">Disetujui Orang Tua / Wali</div>
                    <div className="ead-sig-space ead-sig-space-empty">
                      {/* Dikosongkan khusus untuk tanda tangan basah saat lembar dicetak */}
                    </div>
                    <div className="ead-sig-line" />
                    <p className="ead-sig-name">( {parentName} )</p>
                    <p className="ead-sig-role">Parent / Guardian • Orang Tua Siswa</p>
                    <p className="ead-sig-subnote">*Tanda tangan basah asli pada lembar fisik</p>
                  </div>

                  {/* Kolom 2: Admin Admissions */}
                  <div className="ead-sig-col">
                    <div className="ead-sig-col-title">Diverifikasi Panitia PPDB</div>
                    <div className="ead-sig-space">
                      <div className="ead-official-badge">
                        <ShieldCheck size={13} />
                        <span>Admissions Verified</span>
                      </div>
                    </div>
                    <div className="ead-sig-line" />
                    <p className="ead-sig-name">{officerName}</p>
                    <p className="ead-sig-role">{officerRole} • Sekolah Permata Harapan</p>
                    <p className="ead-sig-subnote">Panitia Penerimaan Siswa Baru</p>
                  </div>

                  {/* Kolom 3: Direktur Sekolah */}
                  <div className="ead-sig-col">
                    <div className="ead-sig-col-title">Disahkan Direktur Sekolah</div>
                    <div className="ead-sig-space">
                      <div className="ead-sig-stamp">
                        SEKOLAH<br />PERMATA<br />HARAPAN
                      </div>
                    </div>
                    <div className="ead-sig-line" />
                    <p className="ead-sig-name">{directorName}</p>
                    <p className="ead-sig-role">{directorRole} • Sekolah Permata Harapan</p>
                    <p className="ead-sig-subnote">Official Directorate Seal</p>
                  </div>
                </div>
              </div>

              {/* FOOTER HALAMAN 2 (LPTI UNPAM CONCEPT) */}
              <div className="ead-kop-footer">
                <div className="ead-kop-footer-divider" />
                <div className="ead-kop-footer-content">
                  <div className="ead-kop-footer-left">
                    <div><strong>Sekolah Permata Harapan • Official Admissions &amp; Financial Governance Document</strong></div>
                    <div>Dokumen resmi ini disahkan oleh Direktorat Penerimaan Siswa Baru Sekolah Permata Harapan.</div>
                    <div><strong>Official Portal:</strong> https://permataharapansecondary.sch.id • Helpdesk: (021) 5890 1100</div>
                  </div>
                  <div className="ead-kop-footer-right">
                    <span className="ead-kop-page-num">- PAGE 2 OF 2 -</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * 2. OFFICIAL CERTIFICATE OF ADMISSION & STUDENT ENROLLMENT (Bukti Siswa Resmi)
 * Formal English Certificate confirming the child is a registered student with NIS
 */
export function OfficialEnrollmentCertificateModal({ app, onClose }) {
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  if (!app) return null;

  const childName = app.child_full_name || app.student_name || app.name || 'Student Candidate';
  const nis = app.student_id_number || `PHS-2026-${String(app.id || 1).padStart(4, '0')}`;
  const enrolledDate = formatFormalDate(app.enrolled_at || app.payment_verified_at || app.updated_at);
  const academicYear = app.academic_year || '2026/2027';
  const gradeTarget = app.grade_target || app.level_name || 'Primary School';
  const levelName = app.level || app.level_name || 'Sekolah Permata Harapan';
  const campus = app.campus_location || 'Campus 1 (Pos Pengumben) - Jl. Pos Pengumben Lama No.13';

  const officerName = app.admissions_officer_name || 'Ivana';
  const officerRole = 'Admissions Officer';
  const directorName = app.school_director_name || app.school_principal_name || (['preschool', 'kindergarten'].includes(String(app.level_code || '').toLowerCase()) ? 'Ratna Sari, S.Pd., M.Pd' : 'Asnah Roidah Yulianti, S.Si., M.Pd');
  const rawDirectorRole = app.school_director_title || app.school_principal_title || 'School Director';
  const directorRole = rawDirectorRole === 'School Principal' ? 'School Director' : rawDirectorRole;

  const formData = app.form_data || {};
  const birthPlace = app.birth_place || formData.birth_place || '-';
  const birthDate = formatFormalDate(app.birth_date || formData.birth_date);
  const gender = app.gender || formData.gender || '-';
  const nationality = app.nationality || formData.nationality || 'Indonesian (WNI)';

  const handlePrint = () => {
    const originalTitle = document.title;
    document.title = `Official_Student_Enrollment_Certificate_${childName.replace(/\s+/g, '_')}`;
    window.print();
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  const handleDownloadPdf = async () => {
    const sheetEl = document.querySelector('.ead-sheet-cert');
    if (!sheetEl) return;
    try {
      setDownloadingPdf(true);
      const html2pdf = (await import('html2pdf.js')).default;
      const opt = {
        margin: [5, 5, 5, 5],
        filename: `Official_Student_Enrollment_Certificate_${childName.replace(/\s+/g, '_')}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      await html2pdf().set(opt).from(sheetEl).save();
    } catch (err) {
      console.error('PDF error, fallback to print:', err);
      handlePrint();
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="ead-modal-overlay" onClick={onClose}>
      <div className="ead-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="ead-modal-toolbar">
          <div className="ead-toolbar-title">
            <CheckCircle2 size={18} />
            <span>Official Student Enrollment Certificate - {childName}</span>
          </div>
          <div className="ead-toolbar-actions">
            <button
              type="button"
              className="ead-btn-download"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
            >
              <Download size={15} />
              <span>{downloadingPdf ? 'Menyimpan PDF...' : 'Simpan / Unduh PDF (.pdf)'}</span>
            </button>
            <button type="button" className="ead-btn-print" onClick={handlePrint}>
              <Printer size={15} />
              <span>Cetak Dokumen</span>
            </button>
            <button type="button" className="ead-btn-close" onClick={onClose} aria-label="Tutup">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="ead-paper-scroll">
          <div className="ead-sheet ead-sheet-cert" style={{ border: '2px solid #0f172a', padding: '44px 50px' }}>
            {/* Header */}
            <div className="ead-header" style={{ borderBottom: '3px double #0f172a' }}>
              <div className="ead-header-inner">
                <div className="ead-brand-col">
                  <div className="ead-logo-box">
                    <img
                      src="/logo_phs.png"
                      alt="Sekolah Permata Harapan Logo"
                      className="ead-school-logo"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="ead-logo-badge" style={{ display: 'none' }}>PHS</div>
                  </div>
                  <div className="ead-brand-text">
                    <h1 style={{ letterSpacing: '0.05em' }}>Sekolah Permata Harapan</h1>
                    <p>Office of the Registrar &amp; Student Administration</p>
                  </div>
                </div>
                <div className="ead-contact-col">
                  <strong>Sekolah Permata Harapan</strong><br />
                  Jl. Pos Pengumben Lama No. 13, Srengseng, Kembangan, Jakarta Barat 11630<br />
                  Phone: (021) 5890 1100 • Email: admissions@permataharapansecondary.sch.id<br />
                  Website: https://permataharapansecondary.sch.id
                </div>
              </div>
            </div>

            {/* Document Title */}
            <div className="ead-doc-title-block" style={{ margin: '24px 0 28px' }}>
              <div style={{ textTransform: 'uppercase', letterSpacing: '0.15em', fontSize: '0.8rem', color: '#64748b', fontWeight: 700 }}>
                Official Academic Credential
              </div>
              <h2 className="ead-doc-title" style={{ fontSize: '1.6rem', marginTop: '6px' }}>
                Certificate of Official Enrollment
              </h2>
              <div className="ead-doc-subtitle" style={{ fontSize: '0.9rem', color: '#334155' }}>
                This is to certify that the individual named below is a duly enrolled student of Sekolah Permata Harapan
              </div>
            </div>

            {/* Certificate Body */}
            <p className="ead-paragraph" style={{ textAlign: 'center', fontSize: '0.95rem', margin: '0 0 20px' }}>
              The Office of Admissions &amp; Registrar hereby certifies and confirms that:
            </p>

            <div className="ead-highlight-box" style={{ background: '#fdfdfe', border: '2px solid #0f172a', padding: '24px 28px' }}>
              <div className="ead-grid-2">
                <div className="ead-grid-item" style={{ gridColumn: 'span 2', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>
                  <span className="ead-grid-label">Full Name of Student</span>
                  <span className="ead-grid-value" style={{ fontSize: '1.25rem', color: '#0f172a' }}>{childName}</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Student Identification Number (NIS)</span>
                  <span className="ead-grid-value" style={{ color: '#2563eb', fontSize: '1.1rem' }}>{nis}</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Academic Program &amp; Level</span>
                  <span className="ead-grid-value">{gradeTarget} ({levelName})</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Place &amp; Date of Birth</span>
                  <span className="ead-grid-value">{birthPlace}, {birthDate}</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Gender &amp; Nationality</span>
                  <span className="ead-grid-value">{gender} / {nationality}</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Enrolled Campus Location</span>
                  <span className="ead-grid-value">{campus}</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Academic Year</span>
                  <span className="ead-grid-value">{academicYear}</span>
                </div>
              </div>
            </div>

            <p className="ead-paragraph" style={{ marginTop: '20px' }}>
              The student named above has completed all required entrance evaluations, submitted verified documentation, and fulfilled all admission prerequisites and institutional matriculation protocols. In witness whereof, the student is officially admitted and listed in the active registry of Sekolah Permata Harapan with all associated academic rights and institutional privileges.
            </p>

            <div className="ead-meta-bar" style={{ marginTop: '24px', border: 'none', background: '#f8fafc', padding: '8px 16px', borderRadius: '4px' }}>
              <div><strong>Effective Date of Enrollment:</strong> {enrolledDate}</div>
              <div><strong>Registry Status:</strong> <span style={{ color: '#059669', fontWeight: 700 }}>Active &amp; Verified</span></div>
            </div>

            {/* Signatures */}
            <div className="ead-signature-section" style={{ marginTop: '32px' }}>
              <div className="ead-sig-col">
                <div className="ead-sig-space">
                  <div className="ead-official-badge">
                    <ShieldCheck size={14} />
                    <span>Academic Registrar</span>
                  </div>
                </div>
                <div className="ead-sig-line" />
                <p className="ead-sig-name">{officerName}</p>
                <p className="ead-sig-role">{officerRole} • Office of the Registrar</p>
              </div>

              <div className="ead-sig-col">
                <div className="ead-sig-space">
                  <div className="ead-sig-stamp">
                    SEKOLAH<br />PERMATA<br />HARAPAN
                  </div>
                </div>
                <div className="ead-sig-line" />
                <p className="ead-sig-name">{directorName}</p>
                <p className="ead-sig-role">{directorRole} • Sekolah Permata Harapan</p>
              </div>
            </div>

            {/* Footer */}
            <div className="ead-footer-note">
              This certificate confirms official student status and is recognized for official transfer, consular, and academic identification purposes.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * 3. OFFICIAL PAYMENT RECEIPT & STATEMENT OF ACCOUNT (Kwitansi Pembayaran Resmi)
 * Formal English Receipt for Tuition & Development Fees (No Allocation / Period column)
 */
export function OfficialPaymentReceiptModal({ app, onClose }) {
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  if (!app) return null;

  const childName = app.child_full_name || app.student_name || app.name || 'Student Candidate';
  const parentName = app.parent_info?.full_name || app.applicant_name || 'Parent / Guardian';
  const nis = app.student_id_number || `PHS-2026-${String(app.id || 1).padStart(4, '0')}`;
  const receiptNo = app.receipt_number || app.invoice?.receipt_no || `RCP-PH/2026/${app.id || '0001'}`;
  const verifiedDate = formatFormalDate(app.payment_verified_at || app.payment_submitted_at || app.updated_at);
  const paymentMethod = app.payment_method || 'Bank Transfer (BCA - Bank Central Asia)';
  const academicYear = app.academic_year || '2026/2027';
  const gradeTarget = app.grade_target || app.level_name || 'Primary School';

  const officerName = app.admissions_officer_name || 'Ivana';
  const financeName = app.finance_pic_name || 'Zahra, S.E.';
  const financeRole = app.finance_pic_title || 'Finance & Treasury PIC';

  const inv = app.invoice || {};
  const regFee = Number(inv.registration_fee || 0);
  const admissionFee = Number(inv.admission_fee || 0);
  const devFee = Number(inv.development_fee || 0);
  const booksFee = Number(inv.books_activity_fee || 0);
  const tuitionFee = Number(inv.tuition_fee || 0);
  const discountAmount = Number(inv.discount_amount || app.discount_amount || 0);
  const totalPayable = Number(inv.total_payable || 0);
  const amountPaid = Number(app.payment_amount_paid || inv.amount_paid || totalPayable);

  const handlePrint = () => {
    const originalTitle = document.title;
    document.title = `Official_Payment_Receipt_${childName.replace(/\s+/g, '_')}`;
    window.print();
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  const handleDownloadPdf = async () => {
    const sheetEl = document.querySelector('.ead-sheet-receipt');
    if (!sheetEl) return;
    try {
      setDownloadingPdf(true);
      const html2pdf = (await import('html2pdf.js')).default;
      const opt = {
        margin: [5, 5, 5, 5],
        filename: `Official_Payment_Receipt_${childName.replace(/\s+/g, '_')}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      await html2pdf().set(opt).from(sheetEl).save();
    } catch (err) {
      console.error('PDF error, fallback to print:', err);
      handlePrint();
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="ead-modal-overlay" onClick={onClose}>
      <div className="ead-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="ead-modal-toolbar">
          <div className="ead-toolbar-title">
            <CreditCard size={18} />
            <span>Official Payment Receipt &amp; Invoice - {childName}</span>
          </div>
          <div className="ead-toolbar-actions">
            <button
              type="button"
              className="ead-btn-download"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
            >
              <Download size={15} />
              <span>{downloadingPdf ? 'Menyimpan PDF...' : 'Simpan / Unduh PDF (.pdf)'}</span>
            </button>
            <button type="button" className="ead-btn-print" onClick={handlePrint}>
              <Printer size={15} />
              <span>Cetak Dokumen</span>
            </button>
            <button type="button" className="ead-btn-close" onClick={onClose} aria-label="Tutup">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="ead-paper-scroll">
          <div className="ead-sheet ead-sheet-receipt">
            {/* Header */}
            <div className="ead-header">
              <div className="ead-header-inner">
                <div className="ead-brand-col">
                  <div className="ead-logo-box">
                    <img
                      src="/logo_phs.png"
                      alt="Sekolah Permata Harapan Logo"
                      className="ead-school-logo"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="ead-logo-badge" style={{ display: 'none' }}>PHS</div>
                  </div>
                  <div className="ead-brand-text">
                    <h1>Sekolah Permata Harapan</h1>
                    <p>Bursar &amp; Student Financial Services Directorate</p>
                  </div>
                </div>
                <div className="ead-contact-col">
                  <strong>Yayasan Pendidikan Permata Harapan</strong><br />
                  Jl. Pos Pengumben Lama No. 13, Srengseng, Kembangan, Jakarta Barat 11630<br />
                  Tax ID / NPWP: 01.345.678.9-082.000 • Phone: (021) 5890 1100<br />
                  Email: admissions@permataharapansecondary.sch.id
                </div>
              </div>
            </div>

            {/* Reference Bar */}
            <div className="ead-meta-bar">
              <div><strong>Receipt Number:</strong> <span style={{ color: '#2563eb' }}>{receiptNo}</span></div>
              <div><strong>Payment Verified Date:</strong> {verifiedDate}</div>
            </div>

            {/* Title */}
            <div className="ead-doc-title-block">
              <h2 className="ead-doc-title">Official Payment Receipt</h2>
              <div className="ead-doc-subtitle">Statement of Academic Account &amp; Settlement Confirmation</div>
            </div>

            {/* Account Details Box */}
            <div className="ead-highlight-box" style={{ background: '#f8fafc' }}>
              <div className="ead-grid-2">
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Received From (Payor)</span>
                  <span className="ead-grid-value">{parentName}</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Student Name &amp; NIS</span>
                  <span className="ead-grid-value">{childName} (NIS: {nis})</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Program &amp; Academic Year</span>
                  <span className="ead-grid-value">{gradeTarget} - TA {academicYear}</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Settlement Channel</span>
                  <span className="ead-grid-value">{paymentMethod}</span>
                </div>
              </div>
            </div>

            {/* Itemized Table without Allocation / Period */}
            <table className="ead-table">
              <thead>
                <tr>
                  <th style={{ width: '45px' }}>No</th>
                  <th>Fee Description</th>
                  <th className="text-right" style={{ width: '180px' }}>Amount (IDR)</th>
                </tr>
              </thead>
              <tbody>
                {admissionFee > 0 && (
                  <tr>
                    <td>1</td>
                    <td>Admission &amp; Matriculation Fee</td>
                    <td className="text-right">{formatCurrencyIDR(admissionFee + regFee)}</td>
                  </tr>
                )}
                {devFee > 0 && (
                  <tr>
                    <td>2</td>
                    <td>Development Fee (Uang Pangkal)</td>
                    <td className="text-right">{formatCurrencyIDR(devFee)}</td>
                  </tr>
                )}
                {booksFee > 0 && (
                  <tr>
                    <td>3</td>
                    <td>Books, Learning Materials &amp; Activity Fee</td>
                    <td className="text-right">{formatCurrencyIDR(booksFee)}</td>
                  </tr>
                )}
                {tuitionFee > 0 && (
                  <tr>
                    <td>4</td>
                    <td>Monthly Tuition Fee (SPP)</td>
                    <td className="text-right">{formatCurrencyIDR(tuitionFee)}</td>
                  </tr>
                )}
                {discountAmount > 0 && (
                  <tr className="ead-discount-row">
                    <td>-</td>
                    <td>Approved Institutional Discount / Voucher ({inv.discount_label || app.promo_code_used || 'Approved Incentive'})</td>
                    <td className="text-right">-{formatCurrencyIDR(discountAmount)}</td>
                  </tr>
                )}
                <tr className="ead-total-row">
                  <td colSpan="2">TOTAL AMOUNT PAID &amp; VERIFIED</td>
                  <td className="text-right" style={{ color: '#059669', fontSize: '1.05rem' }}>
                    {formatCurrencyIDR(amountPaid > 0 ? amountPaid : totalPayable)}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Payment Status Stamp */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '20px 0', background: '#ecfdf5', padding: '12px 18px', borderRadius: '6px', border: '1px solid #a7f3d0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <CheckCircle2 size={22} color="#059669" />
                <div>
                  <strong style={{ color: '#065f46', fontSize: '0.9rem' }}>STATUS: PAYMENT VERIFIED &amp; CLEARED</strong><br />
                  <span style={{ fontSize: '0.78rem', color: '#047857' }}>
                    Funds credited to Yayasan Pendidikan Permata Harapan (BCA Account 548-098-7788)
                  </span>
                </div>
              </div>
              <div style={{ textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 800, color: '#059669', border: '2px solid #059669', padding: '4px 10px', borderRadius: '4px', letterSpacing: '0.08em' }}>
                OFFICIAL PAID
              </div>
            </div>

            <p className="ead-paragraph" style={{ fontSize: '0.78rem', color: '#64748b', fontStyle: 'italic', margin: '14px 0 0' }}>
              * Important Institutional Notice: In accordance with standard school admissions regulations, all payments made to Yayasan Pendidikan Permata Harapan are strictly non-refundable and non-transferable under any circumstances once processed.
            </p>

            {/* Signatures */}
            <div className="ead-signature-section">
              <div className="ead-sig-col">
                <div className="ead-sig-space">
                  <div className="ead-official-badge">
                    <ShieldCheck size={14} />
                    <span>Admissions Verification</span>
                  </div>
                </div>
                <div className="ead-sig-line" />
                <p className="ead-sig-name">{officerName}</p>
                <p className="ead-sig-role">Admissions &amp; Verification Officer</p>
              </div>

              <div className="ead-sig-col">
                <div className="ead-sig-space">
                  <div className="ead-sig-stamp">
                    YAYASAN<br />PERMATA<br />HARAPAN
                  </div>
                </div>
                <div className="ead-sig-line" />
                <p className="ead-sig-name">{financeName}</p>
                <p className="ead-sig-role">{financeRole} • Yayasan Pendidikan Permata Harapan</p>
              </div>
            </div>

            {/* Footer */}
            <div className="ead-footer-note">
              This receipt is computer-generated and officially acknowledged by the Bursar Office of Sekolah Permata Harapan.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * 4. KARTU TANDA PESERTA TES & OBSERVASI PPDB (OFFICIAL EXAMINATION PASS)
 * Official Admission Test & Observation Card for offline entrance assessment
 */
export function ExaminationPassModal({ app, onClose }) {
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  if (!app) return null;

  const childName = app.child_full_name || app.student_name || app.name || 'Calon Siswa';
  const regNo = app.registration_no || `REG-PHS-${app.id || '0001'}`;
  const periodName = app.period_name || 'Gelombang 1 (Early Bird)';
  const academicYear = app.academic_year || '2026/2027';
  const gradeTarget = app.grade_target || app.level_name || 'Secondary 1 (Grade 7)';
  const levelName = app.level_name || app.level || 'Secondary School / SMP';

  const testSched = app.test_schedule || {};
  const slotName = testSched.slot_name || app.test_slot_name || 'Sesi Tes & Observasi Masuk';
  const testDate = formatFormalDate(testSched.test_date || app.test_date);
  const testTime = testSched.start_time && testSched.end_time
    ? `${testSched.start_time} - ${testSched.end_time} WIB`
    : app.test_time || '09:00 - 11:30 WIB';
  const campus = testSched.campus_location || app.campus_location || 'Campus 1 (Pos Pengumben) - Jl. Pos Pengumben Lama No.13';
  const room = testSched.room_name || app.room_name || 'Ruang Observasi & Akademik';
  const assessmentTitle = testSched.assessment_title || app.assessment_title || app.assessment_info?.title || 'Cambridge Entrance Assessment & Academic Interview';
  const assessmentSubjects = testSched.assessment_subjects || app.assessment_subjects || app.assessment_info?.subjects || 'English Proficiency, Mathematics, Integrated Science & Family Interview';
  const assessmentPreparation = testSched.assessment_preparation || app.assessment_preparation || app.assessment_info?.preparation || '';
  const assessmentRulesRaw = testSched.assessment_rules || app.assessment_rules || app.assessment_info?.rules || '';
  const parentInstructions = testSched.parent_instructions || app.parent_instructions || app.assessment_info?.parent_instructions || '';

  const preparationList = assessmentPreparation
    ? assessmentPreparation.split(/\r?\n/).map(line => line.replace(/^[\d+\.\-\*\•\s]+/, '').trim()).filter(Boolean)
    : [];

  const rulesList = assessmentRulesRaw
    ? assessmentRulesRaw.split(/\r?\n/).map(line => line.replace(/^[\d+\.\-\*\•\s]+/, '').trim()).filter(Boolean)
    : [
        'Peserta wajib hadir di lokasi kampus Sekolah Permata Harapan minimal 15 (lima belas) menit sebelum waktu ujian dimulai.',
        'Membawa Kartu Tanda Peserta ini (telah dicetak) dan alat tulis lengkap (pensil 2B, penghapus, penggaris, dan pulpen).',
        'Mengenakan pakaian seragam sekolah asal atau pakaian rapi, sopan, berkerah, dan bersepatu.',
        'Kartu Tanda Peserta ini wajib ditunjukkan kepada Petugas Registrasi & Pengawas Ruang pada saat verifikasi kehadiran.',
        'Orang tua / wali pendamping wajib menunggu di area drop-off / ruang tunggu yang telah disediakan oleh panitia.'
      ];

  const formData = app.form_data || {};
  const birthPlace = app.birth_place || formData.birth_place || 'Jakarta';
  const birthDate = formatFormalDate(app.birth_date || formData.birth_date);
  const gender = (app.gender || formData.gender || '').toLowerCase() === 'female' ? 'Perempuan' : 'Laki-laki';
  const previousSchool = app.previous_school || formData.previous_school || '-';
  const parentName = app.father_name || app.mother_name || app.parent_info?.full_name || app.applicant_name || 'Orang Tua / Wali';
  const parentPhone = app.parent_phone || formData.parent_phone || '-';
  const photoUrl = app.student_photo_url || app.pass_photo_url || app.photo_url || formData.student_photo_url;
  const officerName = app.admissions_officer_name || 'Panitia Penerimaan Siswa Baru';

  const handlePrint = () => {
    const originalTitle = document.title;
    document.title = `Kartu_Peserta_Tes_${childName.replace(/\s+/g, '_')}`;
    window.print();
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  const handleDownloadPdf = async () => {
    const sheetEl = document.querySelector('.ead-sheet-exam');
    if (!sheetEl) return;
    try {
      setDownloadingPdf(true);
      const html2pdf = (await import('html2pdf.js')).default;
      const opt = {
        margin: [5, 5, 5, 5],
        filename: `Kartu_Peserta_Tes_${childName.replace(/\s+/g, '_')}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      await html2pdf().set(opt).from(sheetEl).save();
    } catch (err) {
      console.error('PDF error, fallback to print:', err);
      handlePrint();
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="ead-modal-overlay" onClick={onClose}>
      <div className="ead-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="ead-modal-toolbar">
          <div className="ead-toolbar-title">
            <Award size={18} />
            <span>Kartu Tanda Peserta Tes &amp; Observasi PPDB - {childName}</span>
          </div>
          <div className="ead-toolbar-actions">
            <button
              type="button"
              className="ead-btn-download"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
            >
              <Download size={15} />
              <span>{downloadingPdf ? 'Menyimpan PDF...' : 'Simpan / Unduh PDF (.pdf)'}</span>
            </button>
            <button type="button" className="ead-btn-print" onClick={handlePrint}>
              <Printer size={15} />
              <span>Cetak Dokumen</span>
            </button>
            <button type="button" className="ead-btn-close" onClick={onClose} aria-label="Tutup">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="ead-paper-scroll">
          <div className="ead-sheet ead-sheet-exam">
            {/* Header Kop Surat */}
            <div className="ead-header" style={{ paddingBottom: 12 }}>
              <div className="ead-header-inner">
                <div className="ead-brand-col">
                  <div className="ead-logo-box">
                    <img
                      src="/logo_phs.png"
                      alt="Sekolah Permata Harapan Logo"
                      className="ead-school-logo"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="ead-logo-badge" style={{ display: 'none' }}>PHS</div>
                  </div>
                  <div className="ead-brand-text">
                    <h1>Sekolah Permata Harapan</h1>
                    <p>Panitia Penerimaan Peserta Didik Baru (PPDB)</p>
                  </div>
                </div>
                <div className="ead-contact-col">
                  <strong>Sekolah Permata Harapan</strong><br />
                  Jl. Pos Pengumben Lama No. 13, Srengseng, Kembangan, Jakarta Barat 11630<br />
                  Phone: (021) 5890 1100 • Email: admissions@permataharapansecondary.sch.id<br />
                  Website: https://permataharapansecondary.sch.id
                </div>
              </div>
            </div>

            {/* Document Title Banner */}
            <div className="ead-exam-title-banner">
              <h2>KARTU TANDA PESERTA SELEKSI MASUK &amp; OBSERVASI</h2>
              <div className="ead-exam-subtitle">
                Tahun Pelajaran {academicYear} • {levelName}
              </div>
            </div>

            {/* Two-Column Candidate Profile & Photo Section */}
            <div className="ead-exam-profile-grid">
              <div className="ead-exam-photo-col">
                <div className="ead-exam-photo-card">
                  {photoUrl ? (
                    <img src={photoUrl} alt={childName} className="ead-exam-photo-img" />
                  ) : (
                    <div className="ead-exam-photo-placeholder">
                      <User size={36} color="#94a3b8" />
                      <span>Pas Foto 4x3 Calon Siswa</span>
                    </div>
                  )}
                </div>
                <div className="ead-exam-reg-badge">
                  <small>Nomor Registrasi / Peserta</small>
                  <strong>{regNo}</strong>
                </div>
                <div className="ead-exam-period-badge">
                  {periodName}
                </div>
              </div>

              <div className="ead-exam-info-col">
                <table className="ead-exam-table">
                  <tbody>
                    <tr>
                      <td className="ead-col-label">Nama Lengkap Siswa</td>
                      <td className="ead-col-colon">:</td>
                      <td className="ead-col-val"><strong>{childName}</strong></td>
                    </tr>
                    <tr>
                      <td className="ead-col-label">Jenjang &amp; Kelas Dituju</td>
                      <td className="ead-col-colon">:</td>
                      <td className="ead-col-val">{levelName} — {gradeTarget}</td>
                    </tr>
                    <tr>
                      <td className="ead-col-label">Tempat, Tanggal Lahir</td>
                      <td className="ead-col-colon">:</td>
                      <td className="ead-col-val">{birthPlace}, {birthDate}</td>
                    </tr>
                    <tr>
                      <td className="ead-col-label">Jenis Kelamin</td>
                      <td className="ead-col-colon">:</td>
                      <td className="ead-col-val">{gender}</td>
                    </tr>
                    <tr>
                      <td className="ead-col-label">Asal Sekolah</td>
                      <td className="ead-col-colon">:</td>
                      <td className="ead-col-val">{previousSchool}</td>
                    </tr>
                    <tr>
                      <td className="ead-col-label">Nama Orang Tua / Wali</td>
                      <td className="ead-col-colon">:</td>
                      <td className="ead-col-val">{parentName}</td>
                    </tr>
                    <tr>
                      <td className="ead-col-label">Kontak / No. WhatsApp</td>
                      <td className="ead-col-colon">:</td>
                      <td className="ead-col-val">{parentPhone}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Test Schedule & Location Box */}
            <div className="ead-exam-sched-card">
              <div className="ead-exam-sched-head">
                <Calendar size={16} />
                <span>Jadwal &amp; Lokasi Pelaksanaan Tes Offline</span>
              </div>
              <div className="ead-exam-sched-grid">
                <div>
                  <small>Hari &amp; Tanggal</small>
                  <strong>{testDate}</strong>
                </div>
                <div>
                  <small>Waktu Pelaksanaan</small>
                  <strong>{testTime}</strong>
                </div>
                <div>
                  <small>Sesi Tes</small>
                  <strong>{slotName}</strong>
                </div>
                <div>
                  <small>Lokasi Kampus &amp; Ruangan</small>
                  <strong>{campus} ({room})</strong>
                </div>
              </div>
            </div>

            {/* Assessment Focus */}
            <div className="ead-exam-subjects-card">
              <strong>Fokus Materi Asesmen:</strong> {assessmentTitle}
              <div style={{ marginTop: 3, color: '#334155', fontSize: '0.8rem' }}>
                {assessmentSubjects}
              </div>
            </div>

            {/* Required Preparation Items */}
            {preparationList.length > 0 && (
              <div className="ead-exam-prep-card" style={{ marginTop: 8, padding: '8px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6 }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
                  Perlengkapan yang Wajib Dibawa Siswa:
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.75rem', color: '#334155', lineHeight: 1.45 }}>
                  {preparationList.map((item, idx) => (
                    <li key={idx} style={{ marginBottom: 2 }}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Exam Rules & Instructions */}
            <div className="ead-exam-rules-card" style={{ marginTop: 8 }}>
              <h4>Tata Tertib &amp; Ketentuan Peserta Tes:</h4>
              <ol className="ead-exam-rules-list">
                {rulesList.map((rule, idx) => (
                  <li key={idx}>{rule}</li>
                ))}
              </ol>
            </div>

            {/* Special Instructions for Parents / Waiting Area */}
            {parentInstructions && (
              <div style={{ marginTop: 8, padding: '6px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: '0.73rem', color: '#1e40af', lineHeight: 1.4 }}>
                <strong>Catatan Khusus Orang Tua / Pendamping:</strong> {parentInstructions}
              </div>
            )}

            {/* Signatures & QR Code Verification */}
            <div className="ead-exam-sig-row">
              <div className="ead-exam-sig-col">
                <p className="ead-exam-sig-title">Tanda Tangan Calon Siswa,</p>
                <div className="ead-exam-sig-space" />
                <div className="ead-exam-sig-line" />
                <p className="ead-exam-sig-name">{childName}</p>
                <small className="ead-exam-sig-note">Tanda tangan pada saat hadir di ruang tes</small>
              </div>

              <div className="ead-exam-qr-col">
                <div className="ead-qr-frame">
                  <svg width="84" height="84" viewBox="0 0 100 100">
                    <rect width="100" height="100" fill="#ffffff" />
                    <rect x="10" y="10" width="24" height="24" fill="#0f172a" />
                    <rect x="14" y="14" width="16" height="16" fill="#ffffff" />
                    <rect x="18" y="18" width="8" height="8" fill="#0f172a" />
                    <rect x="66" y="10" width="24" height="24" fill="#0f172a" />
                    <rect x="70" y="14" width="16" height="16" fill="#ffffff" />
                    <rect x="74" y="18" width="8" height="8" fill="#0f172a" />
                    <rect x="10" y="66" width="24" height="24" fill="#0f172a" />
                    <rect x="14" y="70" width="16" height="16" fill="#ffffff" />
                    <rect x="18" y="74" width="8" height="8" fill="#0f172a" />
                    <rect x="42" y="14" width="6" height="6" fill="#0f172a" />
                    <rect x="52" y="24" width="6" height="6" fill="#0f172a" />
                    <rect x="42" y="34" width="6" height="6" fill="#0f172a" />
                    <rect x="52" y="44" width="6" height="6" fill="#0f172a" />
                    <rect x="42" y="54" width="6" height="6" fill="#0f172a" />
                    <rect x="52" y="64" width="6" height="6" fill="#0f172a" />
                    <rect x="42" y="74" width="6" height="6" fill="#0f172a" />
                    <rect x="66" y="44" width="6" height="6" fill="#0f172a" />
                    <rect x="76" y="54" width="6" height="6" fill="#0f172a" />
                    <rect x="86" y="64" width="6" height="6" fill="#0f172a" />
                    <rect x="66" y="74" width="6" height="6" fill="#0f172a" />
                    <rect x="76" y="84" width="6" height="6" fill="#0f172a" />
                  </svg>
                </div>
                <span className="ead-qr-caption">Scan Verifikasi Keaslian Kartu</span>
              </div>

              <div className="ead-exam-sig-col">
                <p className="ead-exam-sig-title">Panitia PPDB Sekolah,</p>
                <div className="ead-exam-sig-space">
                  <div className="ead-sig-stamp">
                    PANITIA PPDB<br />PERMATA<br />HARAPAN
                  </div>
                </div>
                <div className="ead-exam-sig-line" />
                <p className="ead-exam-sig-name">{officerName}</p>
                <small className="ead-exam-sig-note">Panitia Penerimaan Peserta Didik Baru</small>
              </div>
            </div>

            {/* Footer */}
            <div className="ead-footer-note" style={{ marginTop: 12 }}>
              Dokumen resmi sistem PPDB Sekolah Permata Harapan. Harap disimpan dengan baik dan dibawa saat tes seleksi berlangsung.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
